import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type { CustomerDto } from "@grizzly/shared";
import { customerStatus } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { useI18n, type MessageKey } from "../i18n/index.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { SelectInput, TextInput } from "../components/ui/field.js";
import { CustomerCreateDialog } from "../components/crm/customerCreateDialog.js";

export function CustomersPage() {
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState<CustomerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(
    null
  );
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listCustomers({
        search: appliedSearch,
        status: status || undefined,
      });
      setItems(res.items);
    } catch (err) {
      const e = err as ApiClientError;
      setError({ message: e.message, requestId: e.requestId });
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setAppliedSearch(search.trim());
  };

  const columns: Column<CustomerDto>[] = [
    {
      key: "name",
      header: t("customers.column.name"),
      render: (row) => (
        <Link
          to={`/customers/${row.id}`}
          className="font-medium text-action-primary-text hover:underline"
        >
          {row.displayName || row.legalName}
          <span className="block text-xs font-normal text-ink-muted">
            {row.code} · {row.legalName}
          </span>
        </Link>
      ),
    },
    {
      key: "phone",
      header: t("customers.column.phone"),
      render: (row) => <span className="tabular">{row.phone ?? "-"}</span>,
    },
    {
      key: "email",
      header: t("customers.column.email"),
      render: (row) => row.email ?? "-",
    },
    {
      key: "status",
      header: t("customers.column.status"),
      render: (row) => (
        <Badge tone={customerStatus(row.status).tone}>
          {t(`status.customer.${row.status}` as MessageKey)}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("customers.title")}
        description={t("customers.description")}
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t("customers.newCustomer")}
          </Button>
        }
      />

      <Card className="mb-4">
        <form onSubmit={submitSearch} className="flex flex-wrap items-end gap-3">
          <div className="min-w-64 flex-1">
            <TextInput
              label={t("common.fields.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              hint={t("customers.searchHint")}
            />
          </div>
          <div className="w-48">
            <SelectInput
              label={t("common.fields.status")}
              options={[
                { value: "", label: t("customers.statusOption.activeAndLeads") },
                { value: "lead", label: t("customers.statusOption.leadsOnly") },
                { value: "active", label: t("customers.statusOption.activeOnly") },
                { value: "archived", label: t("status.customer.archived") },
              ]}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary">
            {t("common.search")}
          </Button>
        </form>
      </Card>

      <DataTable
        caption={t("customers.title")}
        loading={loading}
        error={error}
        onRetry={load}
        rows={items}
        rowKey={(row) => row.id}
        columns={columns}
        emptyTitle={t("customers.emptyTitle")}
        emptyDescription={t("customers.emptyDescription")}
      />

      <CustomerCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => void load()}
      />
    </>
  );
}
